import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { TokenStorageService } from '@/app/auth/services/token-storage.service';
import { UserAccessManagementService } from '../services/user-access-management.service';
import * as UserAccessManagementActions from './user-access-management.actions';
import { ToastService } from '@shared/components/toast/services/toast.service';

@Injectable()
export class UserAccessManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(UserAccessManagementService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  private get invalidResponseError(): string {
    return this.translate.instant('common.errors.invalidResponse');
  }

  /**
   * A 200 whose envelope carries no payload is a failure — and one the error
   * interceptor never saw, so it is toasted here or nowhere (F-185, F-195).
   */
  private invalidResponse<T>(failure: (props: { error: string }) => T): T {
    this.toast.error(this.invalidResponseError);
    return failure({ error: this.invalidResponseError });
  }

  private get genericRequestError(): string {
    return this.translate.instant('common.errors.requestFailed');
  }

  readonly getLoginSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.getLoginSettings),
      switchMap(() =>
        this.service.getLoginSettings().pipe(
          switchMap(res => {
            const response = res?.data;
            if (!response) {
              return of(
                UserAccessManagementActions.getLoginSettingsFailure({
                  error: this.invalidResponseError,
                })
              );
            }
            return of(
              UserAccessManagementActions.getLoginSettingsSuccess({ response })
            );
          }),
          catchError(err =>
            of(
              UserAccessManagementActions.getLoginSettingsFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly updateLoginSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.updateLoginSettings),
      switchMap(({ payload }) =>
        this.service.updateLoginSettings(payload).pipe(
          switchMap(res => {
            const response = res?.data;
            if (!response) {
              // No interceptor toast for a 200 — say it here (F-196).
              return of(
                this.invalidResponse(
                  UserAccessManagementActions.updateLoginSettingsFailure
                )
              );
            }
            return of(
              UserAccessManagementActions.updateLoginSettingsSuccess({
                response,
              })
            );
          }),
          catchError(err =>
            of(
              UserAccessManagementActions.updateLoginSettingsFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  // IP Whitelist Effects
  readonly loadIpWhitelistList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.loadIpWhitelistList),
      switchMap(({ page, limit }) =>
        this.service.getIpWhitelistList(page, limit).pipe(
          map(response =>
            UserAccessManagementActions.loadIpWhitelistListSuccess({ response })
          ),
          catchError(err =>
            of(
              UserAccessManagementActions.loadIpWhitelistListFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly loadIpWhitelistDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.loadIpWhitelistDetail),
      switchMap(({ userId }) =>
        this.service.getIpWhitelistDetail(userId).pipe(
          map(response =>
            response?.data
              ? UserAccessManagementActions.loadIpWhitelistDetailSuccess({
                  response,
                })
              : // A 200 that carries no restriction is not a success (F-185).
                this.invalidResponse(
                  UserAccessManagementActions.loadIpWhitelistDetailFailure
                )
          ),
          catchError(err =>
            of(
              UserAccessManagementActions.loadIpWhitelistDetailFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly createIpRestriction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.createIpRestriction),
      switchMap(({ userId, payload }) =>
        this.service.createIpRestriction(userId, payload).pipe(
          switchMap(res => {
            const response = res?.data;
            if (!response) {
              return of(
                UserAccessManagementActions.createIpRestrictionFailure({
                  error: this.invalidResponseError,
                })
              );
            }
            return of(
              UserAccessManagementActions.createIpRestrictionSuccess({
                response,
              })
            );
          }),
          catchError(err =>
            of(
              UserAccessManagementActions.createIpRestrictionFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly createIpRestrictionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.createIpRestrictionSuccess),
        tap(() => {})
      ),
    { dispatch: false }
  );

  readonly createIpRestrictionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.createIpRestrictionFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  readonly deleteIpRestriction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.deleteIpRestriction),
      switchMap(({ userId }) =>
        this.service.deleteIpRestriction(userId).pipe(
          map(() => UserAccessManagementActions.deleteIpRestrictionSuccess()),
          catchError(err =>
            of(
              UserAccessManagementActions.deleteIpRestrictionFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly deleteIpRestrictionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.deleteIpRestrictionSuccess)
      ),
    { dispatch: false }
  );

  readonly deleteIpRestrictionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.deleteIpRestrictionFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  readonly updateIpRestrictionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.updateIpRestrictionStatus),
      switchMap(({ userId, payload }) =>
        this.service.updateIpRestrictionStatus(userId, payload).pipe(
          switchMap(res => {
            const response = res?.data;
            if (!response) {
              // The interceptor never saw this 200 — say it here (F-195).
              return of(
                this.invalidResponse(
                  UserAccessManagementActions.updateIpRestrictionStatusFailure
                )
              );
            }
            return of(
              UserAccessManagementActions.updateIpRestrictionStatusSuccess({
                response,
              })
            );
          }),
          catchError(err =>
            of(
              UserAccessManagementActions.updateIpRestrictionStatusFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly updateIpRestrictionStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.updateIpRestrictionStatusSuccess)
      ),
    { dispatch: false }
  );

  readonly updateIpRestrictionStatusFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.updateIpRestrictionStatusFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * This feature's own user list (F-182): the IP-restriction user picker and
   * the transaction filter's requester list. Until the action got its own
   * type, the users page's effect answered it instead.
   */
  readonly loadUserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessManagementActions.loadUserList),
      switchMap(({ status, page, limit }) =>
        this.service.getUserList(status, page, limit).pipe(
          map(response =>
            UserAccessManagementActions.loadUserListSuccess({ response })
          ),
          catchError(err =>
            of(
              UserAccessManagementActions.loadUserListFailure({
                error: this.normalizeError(err),
              })
            )
          )
        )
      )
    )
  );

  readonly loadUserListFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserAccessManagementActions.loadUserListFailure),
        tap(() => {
          // Error toast is handled by global error interceptor
        })
      ),
    { dispatch: false }
  );

  /**
   * Extracts a user-readable error from a backend response.
   * Server messages are returned verbatim (already localized via Accept-Language);
   * only the final fallback is sourced from the active translation bundle.
   */
  private normalizeError(err: any): string {
    if (
      err?.error?.message &&
      typeof err.error.message === 'object' &&
      err.error.message.message
    ) {
      return err.error.message.message;
    }

    if (typeof err?.error?.message === 'string') {
      return err.error.message;
    }

    return err?.message || this.genericRequestError;
  }
}
